import React from "react";
import EventsShowcase from "./EventsShowcase";
import FunEventsHighlight from "./FunEventsHighlights";
import RoboEventsShowcase from "./RoboEventsShowcase";
import TypewriterTitle from "../common/TypewriterTitle";
import eventScheduleData from "../../../data/karmatekEventScheduleData";
import { fetchEvents } from "../../../services/firestoreData";
import type { EventRecord } from "../../../types/dataTypes";
import ErrorScreen from "../../common/ErrorScreen";
import { getErrorDetails } from "../../../utils/errorMapper";

const ROBO_EVENT_IDS = new Set(["robo-race", "robo-soccer"]);

const isFunEvent = (event: EventRecord) =>
  String(event.type || "").toLowerCase() === "fun";
const isRoboEvent = (event: EventRecord) =>
  ROBO_EVENT_IDS.has(String(event.id || ""));

const hasKarmatekFlag = (event: EventRecord) => event?.karmatek === true;

const FALLBACK_EVENTS: EventRecord[] = (eventScheduleData.days || []).flatMap(
  (day: any) => day.events || [],
) as EventRecord[];

const KarmatekEvents: React.FC = () => {
  const [events, setEvents] = React.useState<EventRecord[]>(FALLBACK_EVENTS);
  const [loading, _setLoading] = React.useState(false);
  const [error, setError] = React.useState<any | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await fetchEvents({
          publishedOnly: true,
          karmatekOnly: true,
          sortDesc: false,
        });
        if (!mounted) return;
        const karmatekOnly = data.filter(hasKarmatekFlag);
        if (karmatekOnly.length) {
          setEvents(karmatekOnly);
        }
      } catch (err) {
        if (!mounted) return;
        setError(err);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);
  const karmatekEvents = React.useMemo(
    () => events.filter(hasKarmatekFlag),
    [events],
  );

  const featuredEvents = React.useMemo(() => {
    const seen = new Set<string | number>();
    return karmatekEvents
      .filter((event) => !isFunEvent(event) && !isRoboEvent(event))
      .filter((event) => {
        const key = event.slug || event.id;
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [karmatekEvents]);

  return (
    <section className="bg-black pt-5 pb-2 karmatek-district karmatek-district--events">
      <div className="karmatek-sweep" aria-hidden="true" />
      <div className="container">
        <TypewriterTitle
          text="Karmatek 2026 Events"
          className="text-white fw-bold text-center mb-5 display-5 karmatek-section-title"
          speed={35}
        />

        {error && (
          <div className="mb-4" role="status">
            <ErrorScreen {...getErrorDetails(error)} />
          </div>
        )}

        {loading && !featuredEvents.length && (
          <div className="text-center text-muted mb-4">Loading events...</div>
        )}

        {featuredEvents.map((event, index) => (
          <div className="mb-5" key={event.id}>
            <EventsShowcase
              id={event.slug}
              title={event.name}
              subheading={event.subheading}
              description={event.description || event.tagline}
              date={event.date}
              time={
                event.startTime && event.endTime
                  ? `${event.startTime} – ${event.endTime}`
                  : event.startTime
              }
              location={event.location}
              mode={(event as any).mode}
              entryFee={(event as any).entryFee}
              prizePool={(event as any).prizePool}
              awards={(event as any).awards}
              sponsor={(event as any).sponsor || event.partner}
              backgroundImage={event.backgroundImage}
              characterImage={event.characterImage}
              reverse={index % 2 !== 0}
              isAboveFold={index === 0}
            />
          </div>
        ))}

        <RoboEventsShowcase events={karmatekEvents} />
        <FunEventsHighlight events={karmatekEvents} />
      </div>
    </section>
  );
};

export default KarmatekEvents;
